import { HugeiconsIcon } from "@hugeicons/react";
import { CrownIcon, Delete02Icon } from "@hugeicons-pro/core-bulk-rounded";

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectItem, SelectPopup, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/toaster";
import { useAuth } from "@/lib/auth-context";
import { getMembers, getPendingInvites, inviteUser, revokeInvite } from "@/lib/api/invites";

function roleLabel(role: string): string {
  return role === "owner" ? "Eigenaar" : role === "admin" ? "Beheerder" : "Lid";
}

/** Members + invites + invite form — the same Leden design as the
 *  influencer-management dashboard: flush rows, invite strip, band footer. */
export function TeamAccess() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "admin">("member");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const members = useQuery({ queryKey: ["members"], queryFn: getMembers });
  const invites = useQuery({ queryKey: ["invites"], queryFn: getPendingInvites });

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    setInviteError(null);
    try {
      await inviteUser(email, inviteRole);
      setInviteEmail("");
      qc.invalidateQueries({ queryKey: ["invites"] });
      toast.success("Uitnodiging verstuurd", `De link is 7 dagen geldig en werkt één keer.`);
    } catch (err) {
      setInviteError((err as Error).message);
    } finally {
      setInviting(false);
    }
  }

  async function handleRevokeInvite(id: string) {
    try {
      await revokeInvite(id);
      qc.invalidateQueries({ queryKey: ["invites"] });
    } catch (err) {
      toast.error("Intrekken mislukt", (err as Error).message);
    }
  }

  return (
    <div>
      <ul className="divide-y divide-border/40">
        {members.isPending ? (
          <li className="px-6 py-3"><Skeleton className="h-8 w-full" /></li>
        ) : (
          members.data?.map((m) => (
            <li key={m.user_id} className="flex items-center justify-between gap-3 px-6 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {m.full_name || m.email}
                  {m.user_id === user?.id && (
                    <span className="ml-1 text-xs text-muted-foreground">(jij)</span>
                  )}
                </p>
                <p className="truncate text-xs text-muted-foreground">{m.email}</p>
              </div>
              <Badge
                variant={m.role === "owner" ? "default" : "secondary"}
                size="sm"
                className="capitalize"
              >
                {m.role === "owner" && <HugeiconsIcon icon={CrownIcon} size={12} />}
                {roleLabel(m.role)}
              </Badge>
            </li>
          ))
        )}
      </ul>

      {/* Pending invites */}
      {(invites.data?.length ?? 0) > 0 && (
        <>
          <div className="border-t border-border/50 px-6 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground/60">
            Openstaande uitnodigingen
          </div>
          <ul className="divide-y divide-border/40">
            {invites.data?.map((inv) => {
              const expired = new Date(inv.expires_at).getTime() < Date.now();
              return (
                <li key={inv.id} className="flex items-center justify-between gap-3 px-6 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{inv.email}</p>
                    <p className={`text-xs ${expired ? "text-destructive" : "text-muted-foreground"}`}>
                      {expired
                        ? `Verlopen op ${format(new Date(inv.expires_at), "d MMM yyyy", { locale: nl })}`
                        : `Verloopt op ${format(new Date(inv.expires_at), "d MMM yyyy", { locale: nl })}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {expired && (
                      <Badge variant="destructive" size="sm">Verlopen</Badge>
                    )}
                    <Badge variant="outline" size="sm">{roleLabel(inv.role)}</Badge>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => handleRevokeInvite(inv.id)}
                      aria-label="Uitnodiging intrekken"
                    >
                      <HugeiconsIcon icon={Delete02Icon} size={14} />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Invite form */}
      <form onSubmit={handleInvite} className="band-footer px-6 py-4">
        <Label className="text-xs font-medium">Teamgenoot uitnodigen</Label>
        <p className="mt-0.5 text-xs text-muted-foreground">
          De persoon ontvangt een uitnodigingslink voor toegang tot beide Prado-dashboards.
        </p>
        {inviteError && (
          <p className="mt-2 text-xs text-destructive-foreground">{inviteError}</p>
        )}
        <div className="mt-2 flex gap-2">
          <Input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="teammate@example.com"
            required
          />
          <Select
            value={inviteRole}
            onValueChange={(v) => setInviteRole((v as "admin" | "member") ?? "member")}
          >
            <SelectTrigger className="w-36 shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectPopup>
              <SelectItem value="member">Lid</SelectItem>
              <SelectItem value="admin">Beheerder</SelectItem>
            </SelectPopup>
          </Select>
          <Button type="submit" size="sm" disabled={inviting || !inviteEmail.trim()}>
            {inviting ? "Verzenden…" : "Uitnodigen"}
          </Button>
        </div>
      </form>
    </div>
  );
}
